import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Box, Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDate } from "@internationalized/date";
import PageContainer from "../../common_components/PageContainer";
import CommonTable from "../../common_components/CommonTable";
import { Dropdown } from "../../common_components/FormInputs";
import { fetchSalesGoal, syncSalesGoal, setGoalDate, selectSalesGoal, selectSalesGoalLoading, selectSalesGoalSyncing, selectSalesGoalDate, selectSalesGoalLastUpdated, } from "../../redux/salesGoalSlice";
import { useSalesGoalTableConfigs } from "./Component/SalesGoalCells";
import { getRowName, getRowKind, isValidGoalDate } from "./Component/shared";
import SalesGoalHeader from "./Component/SalesGoalHeader";
import SalesGoalExpended from "./Component/SalesGoalExpended";
import ChannelTabs from "../../common_components/ChannelTabs";
const TABS = [
    { value: "AE", label: "AE" },
    { value: "BDR", label: "BDR" },
];
const CHANNEL_TABS = TABS.map((t) => ({ key: t.value, label: t.label }));
// AE-only client-side filter by row category (T = Team, C = Channel, N = Network)
const AE_KIND_OPTIONS = [
    { value: "ALL", label: "All" },
    { value: "T", label: "Team" },
    { value: "C", label: "Channel" },
    { value: "N", label: "Network" },
];
const SalesGoalMain = () => {
    const dispatch = useDispatch();
    const goals = useSelector(selectSalesGoal);
    const loading = useSelector(selectSalesGoalLoading);
    const syncing = useSelector(selectSalesGoalSyncing);
    const goal_date = useSelector(selectSalesGoalDate);
    const lastUpdated = useSelector(selectSalesGoalLastUpdated);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [activeTab, setActiveTab] = useState("AE");
    const [sortConfig, setSortConfig] = useState(null);
    const [aeKindFilter, setAeKindFilter] = useState("ALL");
    const selectedCalendarDate = useMemo(() => {
        if (!goal_date)
            return null;
        const [y, m] = goal_date.split("-").map(Number);
        return new CalendarDate(y, m, 1);
    }, [goal_date]);
    useEffect(() => {
        if (!isValidGoalDate(goal_date))
            return;
        dispatch(fetchSalesGoal({ goal_date }));
    }, [dispatch, goal_date]);
    useEffect(() => {
        setExpandedRowId(null);
        setSortConfig(null);
        setAeKindFilter("ALL");
    }, [activeTab]);
    const handleMonthChange = (v) => {
        if (v === "ALL_TIME")
            return;
        if (!v || !Number.isInteger(v.year) || !Number.isInteger(v.month))
            return;
        if (v.month < 1 || v.month > 12)
            return;
        const monthStr = `${v.year}-${String(v.month).padStart(2, "0")}`;
        if (!isValidGoalDate(monthStr))
            return;
        dispatch(setGoalDate(monthStr));
    };
    const handleRefresh = () => {
        if (!isValidGoalDate(goal_date))
            return;
        dispatch(syncSalesGoal({ goal_date }));
    };
    const handleExpandToggle = (rowId) => {
        setExpandedRowId((prev) => (prev === rowId ? null : rowId));
    };
    const handleSortChange = (columnId, direction) => {
        setSortConfig(direction ? { columnId, direction } : null);
    };
    const filteredAndSortedRows = useMemo(() => {
        let rows = goals
            .map((row, idx) => ({ ...row, id: row._id || `row-${idx}` }))
            .filter((row) => {
            if (activeTab === "AE")
                return row.type === "AE";
            return row.type === "BDR";
        });
        // AE-only category filter (Team / Channel / Network)
        if (activeTab === "AE" && aeKindFilter !== "ALL") {
            rows = rows.filter((row) => getRowKind(row) === aeKindFilter);
        }
        if (sortConfig?.direction) {
            rows = [...rows].sort((a, b) => {
                const nameA = getRowName(a).toLowerCase();
                const nameB = getRowName(b).toLowerCase();
                return sortConfig.direction === "asc"
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            });
        }
        return rows;
    }, [goals, activeTab, sortConfig, aeKindFilter]);
    const { columns, renderCell } = useSalesGoalTableConfigs(activeTab);
    const renderExpandedContent = (row) => _jsx(SalesGoalExpended, { row: row });
    return (_jsx(PageContainer, { children: _jsxs(Box, { className: "sg-page-wrapper", children: [_jsx(SalesGoalHeader, { lastUpdated: lastUpdated, syncing: syncing, selectedCalendarDate: selectedCalendarDate, onRefresh: handleRefresh, onMonthChange: handleMonthChange }), _jsx(Paper, { elevation: 0, className: "sg-table-paper", children: _jsxs(Box, { className: "sg-paper-inner", children: [_jsxs(Box, { className: "sg-tabs-bar", children: [_jsx(Box, { sx: { width: "240px", bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E5E7EB" }, children: _jsx(ChannelTabs, { tabs: CHANNEL_TABS, value: activeTab, onChange: (key) => setActiveTab(key) }) }), activeTab === "AE" && (_jsx(Dropdown, { value: aeKindFilter, onChange: (v) => setAeKindFilter(v), options: AE_KIND_OPTIONS, size: "sm", fullWidth: false, className: "header-dropdown", usePortal: true, displayValue: _jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Category:" }), " ", _jsx("span", { style: { color: "#667085" }, children: AE_KIND_OPTIONS.find((o) => o.value === aeKindFilter)?.label || "All" })] }) }))] }), _jsx(Box, { className: `sg-table-wrapper ${activeTab === "BDR" ? "sg-tab-bdr" : "sg-tab-ae"}`, children: _jsx(CommonTable, { columns: columns, rows: filteredAndSortedRows, renderCell: renderCell, renderExpandedContent: renderExpandedContent, controlledExpandedRows: expandedRowId ? new Set([expandedRowId]) : new Set(), onExpandToggle: handleExpandToggle, expandTrigger: activeTab === "AE" ? "icon" : "none", isRowExpandable: (row) => activeTab === "AE" && getRowKind(row) === "T", isLoading: loading || syncing, stickyHeader: true, maxHeight: "600px", sortConfig: sortConfig, onSortChange: handleSortChange }) })] }) })] }) }));
};
export default SalesGoalMain;
